/**
 * Role Configuration
 *
 * Priority mapping and helper functions for user roles.
 * Higher priority number means higher precedence for redirect logic.
 */

import { UserRole, RoleCategory, type RolePriority } from '@/types';

/**
 * Role priority map - determines which app to redirect to when user has multiple roles
 * Priority order (highest to lowest):
 * 1. Bono Internal roles (100-90) → Operations App
 * 2. Partner roles (80-70) → Operations App
 * 3. ESG roles (60-56) → ESG App
 * 4. Vendor roles (55-53) → Vendor Portal
 * 5. Supply Chain roles (52-50) → Supply Chain App
 * 6. Carbon Accounting roles (49-47) → Carbon Accounting (default)
 * 7. Survey roles (46) → Survey App
 */
export const ROLE_PRIORITY_MAP: Record<UserRole, number> = {
  // Bono Internal (highest priority)
  [UserRole.BONO_SUPER_ADMIN]: 100,
  [UserRole.BONO_ACCOUNT_MANAGER]: 90,

  // Partner roles
  [UserRole.PARTNER_ADMIN]: 80,
  [UserRole.PARTNER_USER]: 70,
  [UserRole.PARTNER_READ_ONLY]: 69,

  // ESG roles
  [UserRole.ESG_ADMIN]: 60,
  [UserRole.ESG_FACILITY_RESPONSIBLE]: 59,
  [UserRole.ESG_REVIEWER]: 58,
  [UserRole.ESG_DATA_CONTRIBUTOR]: 57,
  [UserRole.ESG_READ_ONLY]: 56,

  // Vendor roles
  [UserRole.VENDOR_ADMIN]: 55,
  [UserRole.VENDOR_USER]: 54,
  [UserRole.VENDOR_READ_ONLY]: 53,

  // Supply Chain roles
  [UserRole.SUPPLY_CHAIN_ADMIN]: 52,
  [UserRole.SUPPLY_CHAIN_USER]: 51,
  [UserRole.SUPPLY_CHAIN_READ_ONLY]: 50,

  // Carbon Accounting (default, lowest priority)
  [UserRole.CARBON_ACCOUNTING_ADMIN]: 49,
  [UserRole.CARBON_ACCOUNTING_USER]: 48,
  [UserRole.CARBON_ACCOUNTING_READ_ONLY]: 47,

  // Survey roles
  [UserRole.SURVEY_USER]: 46,
};

/**
 * Default role when user has no specific roles
 */
export const DEFAULT_ROLE = UserRole.CARBON_ACCOUNTING_USER;

/**
 * Bono admin roles (Operations App access)
 */
export const BONO_ADMIN_ROLES: UserRole[] = [
  UserRole.BONO_SUPER_ADMIN,
  UserRole.BONO_ACCOUNT_MANAGER,
  UserRole.PARTNER_ADMIN,
  UserRole.PARTNER_USER,
];

/**
 * ESG roles
 */
export const ESG_ROLES: UserRole[] = [
  UserRole.ESG_ADMIN,
  UserRole.ESG_FACILITY_RESPONSIBLE,
  UserRole.ESG_DATA_CONTRIBUTOR,
  UserRole.ESG_REVIEWER,
  UserRole.ESG_READ_ONLY,
];

/**
 * Vendor roles
 */
export const VENDOR_ROLES: UserRole[] = [
  UserRole.VENDOR_USER,
  UserRole.VENDOR_ADMIN,
  UserRole.VENDOR_READ_ONLY,
];

/**
 * Supply Chain roles
 */
export const SUPPLY_CHAIN_ROLES: UserRole[] = [
  UserRole.SUPPLY_CHAIN_USER,
  UserRole.SUPPLY_CHAIN_ADMIN,
  UserRole.SUPPLY_CHAIN_READ_ONLY,
];

/**
 * Carbon Accounting roles
 */
export const CARBON_ACCOUNTING_ROLES: UserRole[] = [
  UserRole.CARBON_ACCOUNTING_USER,
  UserRole.CARBON_ACCOUNTING_ADMIN,
  UserRole.CARBON_ACCOUNTING_READ_ONLY,
];

/**
 * Survey roles
 */
export const SURVEY_ROLES: UserRole[] = [
  UserRole.SURVEY_USER,
];

/**
 * Get role category
 */
function getRoleCategory(role: UserRole): RoleCategory {
  if (BONO_ADMIN_ROLES.includes(role)) {
    if (role === UserRole.BONO_SUPER_ADMIN || role === UserRole.BONO_ACCOUNT_MANAGER) {
      return RoleCategory.BONO_INTERNAL;
    }
    return RoleCategory.PARTNER;
  }
  if (ESG_ROLES.includes(role)) {
    return RoleCategory.ESG;
  }
  if (VENDOR_ROLES.includes(role)) {
    return RoleCategory.VENDOR;
  }
  if (SUPPLY_CHAIN_ROLES.includes(role)) {
    return RoleCategory.SUPPLY_CHAIN;
  }
  if (SURVEY_ROLES.includes(role)) {
    return RoleCategory.SURVEY;
  }
  return RoleCategory.CARBON_ACCOUNTING;
}

/**
 * Check if user has any Bono admin role
 */
export function isBonoAdmin(roles: UserRole[]): boolean {
  return roles.some(role => BONO_ADMIN_ROLES.includes(role));
}

/**
 * Check if user has any ESG role
 */
export function isESGRole(roles: UserRole[]): boolean {
  return roles.some(role => ESG_ROLES.includes(role));
}

/**
 * Check if user has any Vendor role
 */
export function isVendorRole(roles: UserRole[]): boolean {
  return roles.some(role => VENDOR_ROLES.includes(role));
}

/**
 * Check if user has any Supply Chain role
 */
export function isSupplyChainRole(roles: UserRole[]): boolean {
  return roles.some(role => SUPPLY_CHAIN_ROLES.includes(role));
}

/**
 * Check if user has any Carbon Accounting role
 */
export function isCarbonAccountingRole(roles: UserRole[]): boolean {
  return roles.some(role => CARBON_ACCOUNTING_ROLES.includes(role));
}

/**
 * Check if user has any Survey role
 */
export function isSurveyRole(roles: UserRole[]): boolean {
  return roles.some(role => SURVEY_ROLES.includes(role));
}

/**
 * Get role priority
 */
export function getRolePriority(role: UserRole): number {
  return ROLE_PRIORITY_MAP[role] || 0;
}

/**
 * Get highest priority role from a list
 */
export function getHighestPriorityRole(roles: UserRole[]): UserRole {
  if (roles.length === 0) {
    return DEFAULT_ROLE;
  }

  return roles.reduce((highest, current) => {
    return getRolePriority(current) > getRolePriority(highest) ? current : highest;
  });
}

/**
 * Sort roles by priority (highest first)
 */
export function sortRolesByPriority(roles: UserRole[]): UserRole[] {
  return [...roles].sort((a, b) => getRolePriority(b) - getRolePriority(a));
}

/**
 * Role priorities with categories
 * Note: This must be defined AFTER all role arrays and getRoleCategory function
 * to avoid circular dependency issues
 */
export const ROLE_PRIORITIES: RolePriority[] = Object.entries(ROLE_PRIORITY_MAP).map(
  ([role, priority]) => ({
    role: role as UserRole,
    priority,
    category: getRoleCategory(role as UserRole),
  })
);